import { useRef, type KeyboardEvent } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import type { WebsiteFrameDevice } from '@dukanti/shared';
import { cn } from '@/lib/utils';
import { useWebsiteT } from '../../hooks/use-website-t';
import { FRAMING_FOCUS_RING } from './framing-focus-ring';
import { framingPanelId, framingTabId } from './framing-ids';

export interface FramingTabsProps {
  readonly devices: readonly WebsiteFrameDevice[];
  readonly value: WebsiteFrameDevice;
  readonly onChange: (device: WebsiteFrameDevice) => void;
  /** Prefix of the tab ids; the panel is `${idPrefix}-panel`. */
  readonly idPrefix: string;
  readonly label: string;
  readonly disabled?: boolean;
}

const ICONS = { desktop: Monitor, mobile: Smartphone } as const;

/**
 * Epic #228 P6 (PD12): the Desktop / Mobile tabs of a crop, hand-rolled
 * (`role="tablist"`, roving focus, automatic activation). Arrow keys follow
 * the reading direction of the Admin, Home/End jump to the ends.
 */
export function FramingTabs({
  devices,
  value,
  onChange,
  idPrefix,
  label,
  disabled = false,
}: FramingTabsProps): React.JSX.Element {
  const { t, dir } = useWebsiteT();
  const refs = useRef(new Map<WebsiteFrameDevice, HTMLButtonElement>());

  const select = (device: WebsiteFrameDevice): void => {
    onChange(device);
    refs.current.get(device)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const index = devices.indexOf(value);
    const forward = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const backward = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    let next: WebsiteFrameDevice | undefined;
    if (event.key === forward) next = devices[(index + 1) % devices.length];
    else if (event.key === backward) next = devices[(index - 1 + devices.length) % devices.length];
    else if (event.key === 'Home') next = devices[0];
    else if (event.key === 'End') next = devices[devices.length - 1];
    if (next === undefined) return;
    event.preventDefault();
    select(next);
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className="border-input bg-card inline-flex rounded-lg border p-0.5"
      data-testid="framing-tabs"
    >
      {devices.map((device) => {
        const Icon = ICONS[device];
        const selected = device === value;
        return (
          <button
            key={device}
            ref={(node) => {
              if (node) refs.current.set(device, node);
              else refs.current.delete(device);
            }}
            type="button"
            role="tab"
            id={framingTabId(idPrefix, device)}
            aria-selected={selected}
            aria-controls={framingPanelId(idPrefix)}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            data-testid={`framing-tab-${device}`}
            onClick={() => select(device)}
            onKeyDown={onKeyDown}
            className={cn(
              'inline-flex min-h-9 items-center gap-1.5 rounded-md px-3 text-sm font-medium outline-none transition-colors disabled:opacity-50',
              FRAMING_FOCUS_RING,
              selected
                ? 'bg-admin-lime text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon aria-hidden="true" className="size-4" />
            {t(`admin.websiteTemplates.media.framing.${device}`)}
          </button>
        );
      })}
    </div>
  );
}
